import { FC, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  ComboBox,
  Dropdown,
  InlineNotification,
  RadioButtonGroup,
  RadioButton,
  MultiSelect,
  DatePicker,
  DatePickerInput,
  FormGroup,
  Tag,
  InlineLoading,
} from '@carbon/react';
import { useAtom, useSetAtom } from 'jotai';
import {
  runModalOpenAtom,
  runModalStateIdAtom,
  runModalStatesAtom,
  runModalCountiesAtom,
  promptVersionsAtom,
  previewFormAtom,
  previewStatsAtom,
  createBatchMutationAtom,
  resetCreateBatchAtom,
} from './atoms';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  CreateBatchForm,
  State,
  County,
  BatchMode,
  ProvisioningStatusFilter,
  DocumentTypeFilter,
  PromptVersion,
} from './types';
import {
  initialForm,
  DOCUMENT_TYPE_OPTIONS,
  PROVISIONING_STATUS_OPTIONS,
  formatCategoryLabel,
} from './utils';
import { formatNumber } from 'utils/number';
import styles from './page.module.scss';

type PreviewRow = Record<string, unknown> & { step: string; documents: string };

const previewColumns: DataTableColumnDef<PreviewRow>[] = [
  {
    accessorKey: 'step',
    header: 'Step',
    meta: { type: 'text', align: 'left' as const },
  },
  {
    accessorKey: 'documents',
    header: 'Documents',
    meta: { type: 'text', align: 'right' as const },
  },
];

interface RunBatchModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const filterStateItem = (
  { item, inputValue }: { item: State; inputValue?: string | null },
  selectedState: State | null
) => {
  if (!inputValue) return true;
  if (
    selectedState &&
    inputValue === `${selectedState.name} (${selectedState.abbreviation})`
  )
    return true;
  const search = inputValue.toLowerCase();
  return (
    item.name.toLowerCase().includes(search) ||
    item.abbreviation.toLowerCase().includes(search)
  );
};

const filterCountyItem = (
  { item, inputValue }: { item: County; inputValue?: string | null },
  selectedCounty: County | null
) => {
  if (!inputValue) return true;
  if (selectedCounty && inputValue === selectedCounty.name) return true;
  return item.name.toLowerCase().includes(inputValue.toLowerCase());
};

export const RunBatchModal: FC<RunBatchModalProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [form, setForm] = useState<CreateBatchForm>(initialForm);

  const setModalOpen = useSetAtom(runModalOpenAtom);
  const [stateId, setStateId] = useAtom(runModalStateIdAtom);
  const [statesLoadable] = useAtom(runModalStatesAtom);
  const [countiesLoadable] = useAtom(runModalCountiesAtom);

  const [batchState, submitBatch] = useAtom(createBatchMutationAtom);
  const resetBatch = useSetAtom(resetCreateBatchAtom);

  const [promptVersionsLoadable] = useAtom(promptVersionsAtom);
  const setPreviewForm = useSetAtom(previewFormAtom);
  const [previewLoadable] = useAtom(previewStatsAtom);

  // Debounce preview form updates
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewForm(form);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [open, form, setPreviewForm]);

  useEffect(() => {
    setModalOpen(open);
    if (!open) {
      setStateId(null);
      setPreviewForm(null);
    }
  }, [open, setModalOpen, setStateId, setPreviewForm]);

  useEffect(() => {
    if (batchState.success) {
      setForm(initialForm);
      resetBatch();
      onCreated();
    }
  }, [batchState.success, resetBatch, onCreated]);

  const states = statesLoadable.state === 'hasData' ? statesLoadable.data : [];
  const counties =
    countiesLoadable.state === 'hasData' ? countiesLoadable.data : [];
  const loadingStates = statesLoadable.state === 'loading';
  const loadingCounties = countiesLoadable.state === 'loading';

  const selectedState = states.find((s) => s.id === stateId) ?? null;
  const selectedCounty = counties.find((c) => c.id === form.countyId) ?? null;

  const promptVersions = useMemo(() => {
    return promptVersionsLoadable.state === 'hasData'
      ? promptVersionsLoadable.data.promptVersions
      : [];
  }, [promptVersionsLoadable]);

  const selectedPromptVersions = useMemo(
    () => promptVersions.filter((pv) => form.promptVersionIds.includes(pv.id)),
    [form.promptVersionIds, promptVersions]
  );

  const previewStats =
    previewLoadable.state === 'hasData' ? previewLoadable.data : null;
  const previewLoading = previewLoadable.state === 'loading';

  const handleClose = useCallback(() => {
    resetBatch();
    onClose();
    setForm(initialForm);
  }, [onClose, resetBatch]);

  const handleSubmit = useCallback(() => {
    submitBatch(form);
  }, [form, submitBatch]);

  // Mode is always set, so the form is always submittable.
  // Location and document filters are all optional.
  const isFormValid = true;

  const previewTableData = useMemo<PreviewRow[]>(() => {
    if (!previewStats) return [];
    const rows: PreviewRow[] = [
      {
        step: 'Will run OCR',
        documents: formatNumber(previewStats.unprovisioned, 0, 0),
      },
      {
        step: 'Will run base prompt',
        documents:
          form.mode === 'provision'
            ? formatNumber(previewStats.total, 0, 0)
            : formatNumber(previewStats.unprovisioned, 0, 0),
      },
    ];
    if (
      form.mode === 'provision' &&
      previewStats.onLatestPrompt !== undefined &&
      previewStats.onLatestPrompt !== null &&
      previewStats.onLatestPrompt > 0
    ) {
      rows.push({
        step: '↳ Already on latest prompt',
        documents: formatNumber(previewStats.onLatestPrompt, 0, 0),
      });
    }
    rows.push(
      {
        step: 'Will run post prompts',
        documents: formatNumber(previewStats.total, 0, 0),
      },
      {
        step: 'Have manual interpretations',
        documents: formatNumber(previewStats.withInterpretations, 0, 0),
      },
      {
        step: 'Total matching criteria',
        documents: formatNumber(previewStats.total, 0, 0),
      }
    );
    return rows;
  }, [previewStats, form.mode]);

  return (
    <Modal
      open={open}
      size="lg"
      modalHeading="Run Provisioning Batch"
      primaryButtonText={batchState.loading ? 'Running...' : 'Run Batch'}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={!isFormValid || batchState.loading}
      onRequestSubmit={handleSubmit}
      onRequestClose={handleClose}
    >
      <div className={styles.modalContent}>
        {batchState.error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={batchState.error}
            lowContrast
            hideCloseButton={false}
            onCloseButtonClick={() => resetBatch()}
          />
        )}

        {/* ─── Pipeline Mode ─── */}
        <FormGroup legendText="Pipeline Mode">
          <p className={styles.modeSelectLabel}>
            Select step to run batch from
          </p>
          <RadioButtonGroup
            name="pipeline-mode"
            valueSelected={form.mode}
            onChange={(value) =>
              setForm({ ...form, mode: String(value) as BatchMode })
            }
            orientation="vertical"
          >
            <RadioButton
              id="mode-force-provision"
              value="provision"
              labelText="Provision (Base Prompt)"
            />
            <RadioButton
              id="mode-post-prompts"
              value="post_prompts"
              labelText="Post Prompts"
            />
          </RadioButtonGroup>
          <div className={styles.pipelineViz}>
            <span className={styles.pipelineLabel}>Pipeline:</span>
            <span className={styles.pipelineStepInactive}>
              OCR (Predetermined)
            </span>
            <span className={styles.pipelineArrow}>→</span>
            <span
              className={
                form.mode === 'provision'
                  ? styles.pipelineStepActive
                  : styles.pipelineStepInactive
              }
            >
              Provision
            </span>
            <span className={styles.pipelineArrow}>→</span>
            <span className={styles.pipelineStepActive}>Post Prompts</span>
          </div>
        </FormGroup>

        {/* ─── Location (optional) ─── */}
        <FormGroup legendText="Location (optional)">
          <div className={styles.formRow}>
            <ComboBox<State>
              key={`state-${stateId ?? 'none'}`}
              id="state-select"
              titleText="State"
              items={states}
              itemToString={(item) => {
                return item ? `${item.name} (${item.abbreviation})` : '';
              }}
              selectedItem={selectedState}
              placeholder={loadingStates ? 'Loading states...' : 'All states'}
              disabled={loadingStates}
              shouldFilterItem={(args) => filterStateItem(args, selectedState)}
              onChange={({ selectedItem }) => {
                const newStateId = selectedItem?.id ?? null;
                setStateId(newStateId);
                setForm({ ...form, stateId: newStateId, countyId: null });
              }}
            />

            <ComboBox<County>
              key={`county-${stateId ?? 'none'}-${form.countyId ?? 'none'}`}
              id="county-select"
              titleText="County"
              items={counties}
              itemToString={(item) => (item ? item.name : '')}
              selectedItem={selectedCounty}
              placeholder={
                loadingCounties ? 'Loading counties...' : 'All counties'
              }
              disabled={!stateId || loadingCounties}
              shouldFilterItem={(args) =>
                filterCountyItem(args, selectedCounty)
              }
              onChange={({ selectedItem }) =>
                setForm({ ...form, countyId: selectedItem?.id ?? null })
              }
            />
          </div>

          {stateId && !loadingCounties && counties.length === 0 && (
            <p className={styles.helperText}>
              No counties with eligible documents found in this state.
            </p>
          )}
        </FormGroup>

        {/* ─── Document Filters ─── */}
        <FormGroup legendText="Document Filters">
          <div className={styles.filterSection}>
            <div className={styles.formRow}>
              <Dropdown
                id="provisioning-status-filter"
                titleText="Provisioning Status"
                label="All Documents"
                items={PROVISIONING_STATUS_OPTIONS}
                itemToString={(item: { id: string; label: string } | null) =>
                  item?.label || ''
                }
                selectedItem={
                  PROVISIONING_STATUS_OPTIONS.find(
                    (o) => o.id === form.provisioningStatus
                  ) || PROVISIONING_STATUS_OPTIONS[0]
                }
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: {
                    id: ProvisioningStatusFilter;
                    label: string;
                  };
                }) => {
                  const newStatus = selectedItem.id;
                  setForm({
                    ...form,
                    provisioningStatus: newStatus,
                    // Clear prompt versions when switching to not provisioned
                    promptVersionIds:
                      newStatus === 'not_provisioned'
                        ? []
                        : form.promptVersionIds,
                  });
                }}
              />

              <Dropdown
                id="document-type-filter"
                titleText="Document Type"
                label="All Document Types"
                items={DOCUMENT_TYPE_OPTIONS}
                itemToString={(item: { id: string; label: string } | null) =>
                  item?.label || ''
                }
                selectedItem={
                  DOCUMENT_TYPE_OPTIONS.find(
                    (o) => o.id === form.documentType
                  ) || DOCUMENT_TYPE_OPTIONS[0]
                }
                onChange={({
                  selectedItem,
                }: {
                  selectedItem: {
                    id: DocumentTypeFilter;
                    label: string;
                  };
                }) => setForm({ ...form, documentType: selectedItem.id })}
              />
            </div>

            <MultiSelect
              key={`prompt-versions-${form.provisioningStatus}`}
              id="prompt-version-filter"
              titleText="Prompt Versions"
              label={
                form.provisioningStatus === 'not_provisioned'
                  ? 'N/A — documents have no prompt'
                  : selectedPromptVersions.length > 0
                    ? `${selectedPromptVersions.length} selected`
                    : 'All prompt versions'
              }
              items={promptVersions}
              itemToString={(item: PromptVersion) => {
                return item
                  ? `${formatCategoryLabel(item.category)} v${item.version}${
                      item.default ? ' (current)' : ''
                    }`
                  : '';
              }}
              initialSelectedItems={selectedPromptVersions}
              disabled={form.provisioningStatus === 'not_provisioned'}
              onChange={({
                selectedItems,
              }: {
                selectedItems: PromptVersion[];
              }) =>
                setForm({
                  ...form,
                  promptVersionIds: selectedItems.map((i) => i.id),
                })
              }
            />
            {selectedPromptVersions.length > 0 && (
              <div className={styles.selectedTags}>
                {selectedPromptVersions.map((pv) => (
                  <Tag
                    key={pv.id}
                    type={pv.default ? 'green' : 'gray'}
                    size="sm"
                    filter
                    onClose={() =>
                      setForm({
                        ...form,
                        promptVersionIds: form.promptVersionIds.filter(
                          (id) => id !== pv.id
                        ),
                      })
                    }
                  >
                    {formatCategoryLabel(pv.category)} v{pv.version}
                  </Tag>
                ))}
              </div>
            )}

            <DatePicker
              datePickerType="range"
              value={[
                form.provisionedDateStart
                  ? new Date(form.provisionedDateStart)
                  : '',
                form.provisionedDateEnd
                  ? new Date(form.provisionedDateEnd)
                  : '',
              ]}
              onChange={(dates: Date[]) => {
                setForm({
                  ...form,
                  provisionedDateStart: dates[0]
                    ? dates[0].toISOString()
                    : null,
                  provisionedDateEnd: dates[1] ? dates[1].toISOString() : null,
                });
              }}
            >
              <DatePickerInput
                id="provisioned-date-start"
                labelText="Provisioned After"
                placeholder="mm/dd/yyyy"
              />
              <DatePickerInput
                id="provisioned-date-end"
                labelText="Provisioned Before"
                placeholder="mm/dd/yyyy"
              />
            </DatePicker>
          </div>
        </FormGroup>

        {/* ─── Preview Stats ─── */}
        {previewLoading && (
          <div className={styles.previewStats}>
            <InlineLoading description="Loading preview..." />
          </div>
        )}
        {previewStats && !previewLoading && (
          <div className={styles.previewStats}>
            <DataTable
              data={previewTableData}
              columns={previewColumns}
              getRowId={(row) => row.step}
              config={{
                sorting: false,
                columnResizing: false,
                columnReordering: false,
                columnPinning: false,
                columnVisibility: false,
                globalSearch: false,
                columnFilters: false,
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
